<template>
	<div
		v-if="column.type === 'enum'"
		class="enum-values__container">
		<div class="enum-values__input-controls-container">
			<div class="enum-values__input-container">
				<label :for="`${column.name}-enum-value`">Enum Values</label>
				<InputText
					:id="`${column.name}-enum-value`"
					v-model="enumValuesInputContents"
					placeholder="Enter and submit enum value"
					title="New enum value"
					@keydown.enter.prevent.stop="addEnumValue()" />
			</div>
			<Button
				icon="fal fa-plus"
				outlined
				severity="secondary"
				@click="addEnumValue()" />
		</div>
		<div class="enum-values__values">
			<Chip
				v-for="value in enumValues"
				:key="value"
				removable
				:label="value"
				@remove="removeEnumValue(value)" />
		</div>
	</div>
</template>

<script setup lang="ts">
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import Chip from 'primevue/chip'
import { ref, computed, watch } from 'vue'
import type { EnrichedColumn } from '@codecannon/schemas/schemas/EnrichedColumn'
import { supportedFakerMethods } from '@codecannon/schemas/interfaces/fakerMethods'

const props = defineProps<{
	column: EnrichedColumn
}>()

const enumValuesInputContents = ref<string>('')

watch(
	() => props.column.type,
	() => {
		if (props.column.type === 'enum') return
		if (!props.column.typeConstraints) return
		const enumConstraintIndex = props.column.typeConstraints.findIndex(
			(tc) => tc.type === 'parameter' && tc.name === 'enumValues',
		)
		if (enumConstraintIndex === -1) return
		props.column.typeConstraints.splice(enumConstraintIndex, 1)
	},
)

const enumValues = computed({
	get: getEnumValues,
	set: (values: string[]) => {
		if (!props.column.typeConstraints) {
			props.column.typeConstraints = []
		}
		if (props.column.type !== 'enum') {
			throw new Error('To set enum values the column type should be enum')
		}
		const enumValuesTypeConstraint = props.column.typeConstraints?.find(
			(c) => c.name === 'enumValues',
		)
		if (!enumValuesTypeConstraint) {
			props.column.typeConstraints?.push({
				type: 'parameter',
				name: 'enumValues',
				params: [JSON.stringify(values)],
			})
		} else {
			enumValuesTypeConstraint.params = [JSON.stringify(values)]
		}
		if (props.column.fakerMethod?.method !== 'randomElement') {
			props.column.fakerMethod = supportedFakerMethods['enum'].default
		}
		props.column.fakerMethod.params = [JSON.stringify(values)]
	},
})

function getEnumValues(): string[] {
	let enumValues: string[] = []
	const enumValuesTypeConstraint = props.column.typeConstraints?.find(
		(c) => c.name === 'enumValues',
	)
	if (enumValuesTypeConstraint) {
		enumValues = JSON.parse(enumValuesTypeConstraint.params[0] as string)
	}
	return enumValues
}

function removeEnumValue(value: string) {
	enumValues.value = enumValues.value.filter((v) => v !== value)
}

function addEnumValue() {
	if (enumValuesInputContents.value && !enumValues.value.includes(enumValuesInputContents.value)) {
		const newValue = new Set([
			...enumValues.value,
			...enumValuesInputContents.value.split(',').map((v) => v.trim()),
		])
		enumValues.value = Array.from(newValue)
		enumValuesInputContents.value = ''
	} else {
		enumValuesInputContents.value = ''
	}
}
</script>

<style lang="scss" scoped>
.enum-values__container {
	display: flex;
	flex-direction: column;
	margin-bottom: 10px;

	.enum-values__input-controls-container {
		display: flex;
		gap: 5px;
		align-items: flex-end;

		.enum-values__input-container {
			display: flex;
			flex-direction: column;
			gap: 5px;
			flex: 1;
		}
	}

	.enum-values__values {
		display: flex;
		flex-wrap: wrap;
		margin-top: 8px;
		gap: 8px;
	}
}
</style>
